'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useGetMarkets } from '../generated/hooks/useGetMarkets.gen.js';
import { useMarketConfig, useMarketConfigCatalog } from '../provider/MarketConfigProvider.js';
import { marketPoolSnapshotEntries } from './market-pool-entries.js';
import { onchainTradeDataFromMarketRow, onchainTradeDataFromMarketRows } from './market-pool-projection.js';
import { useLiveMarketRows } from './use-live-market-rows.js';
export { isPoolSnapshotUnavailable, marketPoolSnapshotFromMarketRow, onchainTradeDataFromMarketRow, onchainTradeDataFromMarketRows } from './market-pool-projection.js';
const EMPTY_IDENTITIES = [];
const EMPTY_IDENTITIES_BY_MARKET = {};
export function useMarketPoolQuoteSnapshots(requestedMarkets, t0) {
  const $ = _c(22);
  const expected = t0 === undefined ? EMPTY_IDENTITIES_BY_MARKET : t0;
  const configQuery = useMarketPoolConfig();
  const markets = canonicalMarketSet(requestedMarkets);
  const t1 = markets.length > 0;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const marketQuery = useGetMarkets(marketPoolQueryArgs(markets), t2);
  const rows = useLiveMarketRows(marketQuery.queryKey, marketQuery.rows, marketQuery.entityQueryKey, marketQuery.projectEntityRow);
  const loadingMore = marketQuery.nextPageError === null && (marketQuery.hasNextPage || marketQuery.isFetchingNextPage || marketQuery.isPending);
  let t3;
  if ($[2] !== configQuery || $[3] !== rows) {
    t3 = () => {
      if (configQuery.data === undefined) {
        return;
      }
      for (const row of rows) {
        configQuery.ensure(row.marketConfig);
      }
    };
    $[2] = configQuery;
    $[3] = rows;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== configQuery.data || $[6] !== configQuery.ensure || $[7] !== rows) {
    t4 = [rows, configQuery.data, configQuery.ensure];
    $[5] = configQuery.data;
    $[6] = configQuery.ensure;
    $[7] = rows;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  const projection = projectRows(rows, markets, expected, configQuery.data ?? {});
  let t5;
  if ($[9] !== configQuery || $[10] !== marketQuery) {
    t5 = async function refetch() {
      await Promise.all([marketQuery.refetch(), configQuery.refetch()]);
    };
    $[9] = configQuery;
    $[10] = marketQuery;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const refetch = t5;
  const result = toQueryResult(marketQuery, configQuery, projection, loadingMore, refetch);
  useRemainingMarketPages(marketQuery, markets.length > 0);
  const t6 = rows.some(row_0 => markets.includes(row_0.pubkey) && missingAmmConfig(row_0));
  const t7 = marketPoolSnapshotEntries({
    markets,
    rows,
    snapshots: projection.data,
    configs: configQuery.data,
    marketError: marketQuery.error !== null && marketQuery.error !== undefined,
    marketPending: marketQuery.isPending,
    marketStale: marketQuery.isStale,
    configPending: configQuery.isPending,
    marketUpdatedAt: marketQuery.dataUpdatedAt,
    configUpdatedAt: configQuery.dataUpdatedAt
  });
  let t8;
  if ($[12] !== configQuery.data || $[13] !== configQuery.isPending || $[14] !== result.isPending || $[15] !== rows) {
    t8 = result.isPending || configQuery.isPending && rows.some(row_1 => row_1.status === "trading" && configQuery.data?.[row_1.marketConfig] === undefined);
    $[12] = configQuery.data;
    $[13] = configQuery.isPending;
    $[14] = result.isPending;
    $[15] = rows;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== result || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = {
      ...result,
      hasMissingAmmConfig: t6,
      ...t7,
      isPending: t8
    };
    $[17] = result;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
/** Existing detail consumers retain their public result contract. */
export function useMarketPoolQuoteSnapshot(marketAddress, expectedOutcomeCount, expectedOutcomeIdentities = EMPTY_IDENTITIES, embeddedMarket) {
  const market = marketAddress ?? '';
  const enabled = market.length > 0 && expectedOutcomeCount !== 0;
  const configQuery = useMarketPoolConfig();
  const embeddedConfig = useMarketConfig(embeddedMarket?.marketConfig);
  const batch = useMarketPoolQuoteSnapshots(embeddedMarket === undefined && enabled ? [market] : [], embeddedMarket === undefined && enabled ? {
    [market]: expectedOutcomeIdentities
  } : {});
  if (embeddedMarket === undefined) {
    const data = batch.data?.[market];
    if (data !== undefined) assertOutcomeCount(data, expectedOutcomeCount);
    if (enabled && data === undefined && !batch.isPending) return {
      ...emptyQueryResult(batch.error ?? new Error('market snapshot unavailable'), batch.refetch),
      hasMissingAmmConfig: batch.hasMissingAmmConfig ?? false
    };
    return {
      ...batch,
      data
    };
  }
  const hasMissingAmmConfig = missingAmmConfig(embeddedMarket);
  if (embeddedMarket.status === 'trading' && configQuery.isPending) return pendingConfigResult(configQuery);
  try {
    const data_0 = onchainTradeDataFromMarketRow(embeddedMarket, embeddedConfig);
    assertOutcomeCount(data_0, expectedOutcomeCount);
    assertOutcomeIdentities(data_0, expectedOutcomeIdentities);
    return {
      data: data_0,
      hasMissingAmmConfig,
      error: null,
      isError: false,
      isFetching: embeddedMarket.status === 'trading' && configQuery.isFetching,
      isPending: false,
      // `embeddedMarket` is the live market-detail cache: its reserves are
      // maintained by the market query's realtime subscription. Platform-config
      // freshness must not be reported as stale pool state and block a trade.
      isStale: false,
      dataUpdatedAt: configQuery.dataUpdatedAt,
      errorUpdatedAt: configQuery.errorUpdatedAt,
      failureCount: configQuery.failureCount,
      refetch: configQuery.refetch
    };
  } catch (error) {
    const projectionError = toError(error);
    return {
      ...emptyQueryResult(projectionError, configQuery.refetch),
      hasMissingAmmConfig
    };
  }
}
export function canonicalMarketSet(markets) {
  return [...new Set(markets.filter(market => market.length > 0))].sort();
}
/** Positions span active bonding curves and graduated AMMs. The markets
 * endpoint defaults to trading-only, so valuation lookups must explicitly
 * request both position-bearing lifecycle states. */
export function marketPoolQueryArgs(markets) {
  return {
    pubkeys: canonicalMarketSet(markets).join(','),
    status: 'trading,graduated'
  };
}
function projectRows(rows, markets, expected, config) {
  try {
    const requested = new Set(markets);
    const data = {
      ...onchainTradeDataFromMarketRows(rows.filter(row => requested.has(row.pubkey)), config)
    };
    const unavailable = [];
    for (const market of markets) {
      const tradeData = data[market];
      if (tradeData === undefined) {
        unavailable.push(market);
        continue;
      }
      try {
        assertOutcomeIdentities(tradeData, expected[market] ?? []);
      } catch {
        delete data[market];
        unavailable.push(market);
      }
    }
    return {
      data,
      ...(unavailable.length > 0 ? {
        error: new Error(`market snapshots unavailable: ${unavailable.join(',')}`)
      } : {})
    };
  } catch (error) {
    const projectionError = toError(error);
    return {
      error: projectionError
    };
  }
}
function toQueryResult(marketQuery, configQuery, projection, loadingMore, refetch) {
  const rawError = projection.error ?? marketQuery.nextPageError ?? marketQuery.error ?? (projection.data === undefined ? configQuery.error : null);
  const error = rawError === null || rawError === undefined ? null : toError(rawError);
  const sourceErrorUpdatedAt = Math.max(marketQuery.errorUpdatedAt, configQuery.errorUpdatedAt);
  const projectionUpdatedAt = Math.max(marketQuery.dataUpdatedAt, configQuery.dataUpdatedAt, sourceErrorUpdatedAt);
  return {
    data: projection.data,
    error,
    isError: error !== null,
    isFetching: marketQuery.isFetching || configQuery.isFetching || loadingMore,
    isPending: marketQuery.isPending || projection.data === undefined && configQuery.isPending || loadingMore,
    isStale: marketQuery.isStale || configQuery.isStale,
    dataUpdatedAt: Math.max(marketQuery.dataUpdatedAt, configQuery.dataUpdatedAt),
    // Projection errors are derived from cached query state. Give them the
    // source state's stable timestamp so unrelated parent renders cannot look
    // like fresh failures and retrigger diagnostics.
    errorUpdatedAt: projection.error === undefined ? sourceErrorUpdatedAt : projectionUpdatedAt,
    failureCount: projection.error === undefined ? marketQuery.failureCount : 1,
    refetch
  };
}
function pendingConfigResult(query) {
  const error = query.error === null ? null : toError(query.error);
  return {
    data: undefined,
    error,
    isError: error !== null,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isStale: query.isStale,
    dataUpdatedAt: query.dataUpdatedAt,
    errorUpdatedAt: query.errorUpdatedAt,
    failureCount: query.failureCount,
    refetch: query.refetch
  };
}
function emptyQueryResult(error, refetch) {
  return {
    data: undefined,
    error,
    isError: true,
    isFetching: false,
    isPending: false,
    isStale: false,
    dataUpdatedAt: 0,
    errorUpdatedAt: Date.now(),
    failureCount: 1,
    refetch
  };
}
function useMarketPoolConfig() {
  const $ = _c(10);
  const catalog = useMarketConfigCatalog();
  const t0 = catalog.status === "loading";
  let t1;
  if ($[0] !== catalog.configs || $[1] !== catalog.dataUpdatedAt || $[2] !== catalog.ensure || $[3] !== catalog.error || $[4] !== catalog.errorUpdatedAt || $[5] !== catalog.failureCount || $[6] !== catalog.isFetching || $[7] !== catalog.refetch || $[8] !== t0) {
    t1 = {
      data: catalog.configs,
      ensure: catalog.ensure,
      error: catalog.error,
      isFetching: catalog.isFetching,
      isPending: t0,
      isStale: false,
      dataUpdatedAt: catalog.dataUpdatedAt,
      errorUpdatedAt: catalog.errorUpdatedAt,
      failureCount: catalog.failureCount,
      refetch: catalog.refetch
    };
    $[0] = catalog.configs;
    $[1] = catalog.dataUpdatedAt;
    $[2] = catalog.ensure;
    $[3] = catalog.error;
    $[4] = catalog.errorUpdatedAt;
    $[5] = catalog.failureCount;
    $[6] = catalog.isFetching;
    $[7] = catalog.refetch;
    $[8] = t0;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
function useRemainingMarketPages(query, enabled) {
  const $ = _c(9);
  let t0;
  if ($[0] !== enabled || $[1] !== query) {
    t0 = () => {
      if (!enabled || !query.hasNextPage || query.isFetchingNextPage || query.nextPageError !== null) {
        return;
      }
      query.fetchNextPage().catch(_temp);
    };
    $[0] = enabled;
    $[1] = query;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== enabled || $[4] !== query.fetchNextPage || $[5] !== query.hasNextPage || $[6] !== query.isFetchingNextPage || $[7] !== query.nextPageError) {
    t1 = [enabled, query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage, query.nextPageError];
    $[3] = enabled;
    $[4] = query.fetchNextPage;
    $[5] = query.hasNextPage;
    $[6] = query.isFetchingNextPage;
    $[7] = query.nextPageError;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  useEffect(t0, t1);
}
function _temp() {}
function assertOutcomeCount(data, expected) {
  if (expected !== undefined && data.outcomes.length !== expected) {
    throw new Error('market pool snapshot outcome count mismatch');
  }
}
function assertOutcomeIdentities(data, expected) {
  if (expected.some(identity => data.outcomes.find(outcome => outcome.outcomeIndex === identity.outcomeIndex)?.outcomeMint !== identity.outcomeMint)) {
    throw new Error('market pool snapshot outcome mint identity mismatch');
  }
}
function toError(value) {
  return value instanceof Error ? value : new Error(String(value));
}
function missingAmmConfig(row) {
  if (row.status !== 'graduated') return false;
  const winner = row.outcomes.find(outcome => outcome.graduated);
  return winner === undefined || !('quoteVaultReserveD6' in winner.currentPoolState) || winner.currentPoolState.tradeFeeRateMillionths === undefined;
}